import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles, Check, Edit2, X as XIcon, RefreshCw, Loader2, ThumbsUp, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

function getConfidenceTier(sim) {
  if (sim === null || sim === undefined) return { key: 'unknown', color: 'var(--c-text-muted)' };
  if (sim >= 0.50) return { key: 'high',    color: 'var(--c-resolved, #10b981)' };
  if (sim >= 0.35) return { key: 'medium',  color: 'var(--c-pending,  #f59e0b)' };
  return                      { key: 'low', color: '#ef4444' };
}

export default function AiSuggestionCard({ ticketId, compact = false, onAccept }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [ineligibleReason, setIneligibleReason] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [generating, setGenerating] = useState(false);
  // In compact mode we start COLLAPSED so the card never covers the customer's message.
  // The agent expands with one click when they want to read the suggestion.
  const [expanded, setExpanded] = useState(!compact);

  const fetchOrGenerate = useCallback(async (forceGenerate = false) => {
    if (!ticketId) return;
    setLoading(true);
    try {
      if (!forceGenerate) {
        const { data } = await api.get(`/ai/suggestion/ticket/${ticketId}`);
        if (data?.suggestion) {
          setSuggestion(data.suggestion);
          setIneligibleReason(null);
          setLoading(false);
          return;
        }
      }
      setGenerating(true);
      const { data } = await api.post('/ai/suggest-reply', { ticket_id: ticketId });
      if (data?.eligible && data.suggestion) {
        setSuggestion(data.suggestion);
        setIneligibleReason(null);
      } else {
        setSuggestion(null);
        setIneligibleReason(data?.reason || 'unknown');
      }
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) setIneligibleReason('forbidden');
      else setIneligibleReason('error');
      setSuggestion(null);
    } finally {
      setLoading(false);
      setGenerating(false);
    }
  }, [ticketId]);

  useEffect(() => {
    setDismissed(false);
    setSuggestion(null);
    setIneligibleReason(null);
    setExpanded(!compact);
    fetchOrGenerate(false);
  }, [ticketId, fetchOrGenerate, compact]);

  const updateStatus = async (status, editedText = null) => {
    if (!suggestion?.id) return;
    try {
      await api.post(`/ai/suggestion/${suggestion.id}/decide`, { status, edited_text: editedText });
      setSuggestion(s => s ? { ...s, status } : s);
    } catch (err) {
      console.error('Failed to update suggestion status', err);
    }
  };

  const handleAccept = async () => {
    if (!suggestion) return;
    if (onAccept) onAccept(suggestion.text);
    await updateStatus('accepted');
    if (!compact) toast.success(t('ai.suggestion.accepted'));
  };

  const handleEdit = async () => {
    if (!suggestion) return;
    if (onAccept) onAccept(suggestion.text, { focus: true });
    await updateStatus('accepted');
  };

  const handleUsefulSkip = async () => {
    await updateStatus('useful_skipped');
    if (compact) setExpanded(false);
  };

  const handleReject = async () => {
    await updateStatus('rejected');
    setDismissed(true);
  };

  const handleRegenerate = () => fetchOrGenerate(true);

  const hiddenReasons = new Set(['already_answered', 'no_customer_message', 'disabled', 'forbidden', 'message_too_short', 'empty_completion']);
  if (dismissed || hiddenReasons.has(ineligibleReason)) return null;

  // ─── COMPACT VARIANT (WhatsApp bubble) ──────────────────────────────
  if (compact) {
    if (loading && !suggestion) {
      return (
        <div style={compactPillStyle(null)}>
          <Loader2 size={12} style={{ animation: 'spin 1s linear infinite', color: 'var(--c-text-muted)' }} />
          <span style={{ fontSize: 11.5, color: 'var(--c-text-muted)' }}>
            {generating ? t('ai.suggestion.generating') : t('ai.suggestion.loading')}
          </span>
        </div>
      );
    }
    if (ineligibleReason === 'daily_cap_reached') {
      return (
        <div style={{ ...compactPillStyle('var(--c-pending, #f59e0b)'), color: 'var(--c-pending, #f59e0b)' }}>
          <Sparkles size={12} />
          <span style={{ fontSize: 11.5 }}>{t('ai.suggestion.capReached')}</span>
        </div>
      );
    }
    if (!suggestion) return null;

    const tier = getConfidenceTier(suggestion.similarity);
    const isDecided = suggestion.status && suggestion.status !== 'pending';

    // Collapsed → single-line pill (NEVER covers the conversation)
    if (!expanded) {
      const previewText = (suggestion.text || '').replace(/\s+/g, ' ').slice(0, 60);
      return (
        <div
          onClick={() => setExpanded(true)}
          title={t('ai.suggestion.expandHint')}
          style={{
            ...compactPillStyle(tier.color),
            cursor: 'pointer',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
            <Sparkles size={12} style={{ color: tier.color, flexShrink: 0 }} />
            <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--c-text-muted)', flexShrink: 0 }}>
              IA
            </span>
            <span style={{ fontSize: 10.5, color: tier.color, fontWeight: 600, flexShrink: 0 }}>
              {t(`ai.suggestion.confidence.${tier.key}`)}
            </span>
            {isDecided ? (
              <span style={{ fontSize: 10.5, color: 'var(--c-text-muted)', flexShrink: 0 }}>
                · {statusLabel(suggestion.status, t)}
              </span>
            ) : (
              <span style={{ fontSize: 11, color: 'var(--c-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                · {previewText}{suggestion.text.length > 60 ? '…' : ''}
              </span>
            )}
          </div>
          <ChevronDown size={14} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />
        </div>
      );
    }

    // Expanded view
    return (
      <div style={{
        position: 'relative',
        padding: '8px 10px 8px 12px',
        background: 'var(--c-surface)',
        border: '1px solid var(--c-border)',
        borderLeft: `3px solid ${tier.color}`,
        borderRadius: 8,
        fontSize: 12.5,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <Sparkles size={11} style={{ color: tier.color, flexShrink: 0 }} />
          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--c-text-muted)' }}>
            {t('ai.suggestion.title')}
          </span>
          <span style={{ fontSize: 10, color: tier.color, fontWeight: 600 }}>
            · {t(`ai.suggestion.confidence.${tier.key}`)}
          </span>
          {suggestion.was_reopened && (
            <span title={t('ai.suggestion.reopenedHint')} style={{ fontSize: 10, color: 'var(--c-pending, #f59e0b)', fontWeight: 600 }}>
              · {t('ai.suggestion.reopened')}
            </span>
          )}
          {suggestion.chunks_used > 0 && (
            <span style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>
              · {suggestion.chunks_used}
            </span>
          )}
          <div style={{ flex: 1 }} />
          {!isDecided && (
            <button onClick={handleRegenerate} title={t('ai.suggestion.regenerate')} style={iconBtn} disabled={loading}>
              <RefreshCw size={12} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
            </button>
          )}
          <button onClick={() => setExpanded(false)} title={t('ai.suggestion.collapse')} style={iconBtn}>
            <ChevronUp size={13} />
          </button>
          <button onClick={handleReject} title={t('ai.suggestion.reject')} style={iconBtn}>
            <XIcon size={12} />
          </button>
        </div>

        <div style={{
          color: 'var(--c-text)',
          whiteSpace: 'pre-wrap',
          lineHeight: 1.45,
          maxHeight: 110,
          overflowY: 'auto',
          marginBottom: isDecided ? 0 : 6,
          paddingRight: 2,
        }}>
          {suggestion.text}
        </div>

        {!isDecided ? (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <button onClick={handleAccept} style={primaryMiniBtn} title={t('ai.suggestion.acceptHint')}>
              <Check size={12} /> {t('ai.suggestion.accept')}
            </button>
            <button onClick={handleEdit} style={ghostMiniBtn} title={t('ai.suggestion.editHint')}>
              <Edit2 size={11} /> {t('ai.suggestion.edit')}
            </button>
            <button onClick={handleUsefulSkip} style={ghostMiniBtn} title={t('ai.suggestion.usefulSkipHint')}>
              <ThumbsUp size={11} /> {t('ai.suggestion.usefulSkip')}
            </button>
          </div>
        ) : (
          <div style={{ fontSize: 10, color: 'var(--c-text-muted)', marginTop: 4 }}>
            {statusLabel(suggestion.status, t)}
          </div>
        )}
      </div>
    );
  }

  // ─── FULL VARIANT (TicketDetailPage) ──────────────────────────────
  const shellStyle = {
    background: 'var(--c-surface)',
    border: '1px solid var(--c-border)',
    borderRadius: 'var(--radius-lg, 12px)',
    padding: '14px 16px',
    position: 'relative',
  };

  if (loading && !suggestion) {
    return (
      <div style={shellStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--c-text-muted)', fontSize: 13 }}>
          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
          <span>{generating ? t('ai.suggestion.generating') : t('ai.suggestion.loading')}</span>
        </div>
      </div>
    );
  }

  if (ineligibleReason === 'daily_cap_reached') {
    return (
      <div style={{ ...shellStyle, borderColor: 'var(--c-pending, #f59e0b)' }}>
        <div style={{ fontSize: 13, color: 'var(--c-pending, #f59e0b)' }}>
          {t('ai.suggestion.capReached')}
        </div>
      </div>
    );
  }

  if (!suggestion) return null;

  const tier = getConfidenceTier(suggestion.similarity);
  const isDecided = suggestion.status && suggestion.status !== 'pending';

  return (
    <div style={shellStyle}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'var(--c-brand)', opacity: 0.7, borderRadius: 'var(--radius-lg, 12px) var(--radius-lg, 12px) 0 0' }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Sparkles size={14} style={{ color: 'var(--c-brand)' }} />
          <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--c-text)' }}>
            {t('ai.suggestion.title')}
          </span>
          <span style={{ fontSize: 11, color: tier.color, fontWeight: 600 }}>
            ● {t(`ai.suggestion.confidence.${tier.key}`)}
          </span>
          {suggestion.was_reopened && (
            <span title={t('ai.suggestion.reopenedHint')} style={{ fontSize: 11, color: 'var(--c-pending, #f59e0b)', fontWeight: 600 }}>
              · {t('ai.suggestion.reopened')}
            </span>
          )}
          {suggestion.chunks_used > 0 && (
            <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>
              · {t('ai.suggestion.chunksUsed', { count: suggestion.chunks_used })}
            </span>
          )}
        </div>
        <button onClick={handleReject} className="btn btn-ghost btn-sm" style={{ padding: 4 }} title={t('ai.suggestion.dismiss')}>
          <XIcon size={14} />
        </button>
      </div>

      <div style={{
        fontSize: 13,
        color: 'var(--c-text)',
        whiteSpace: 'pre-wrap',
        background: 'var(--c-bg)',
        border: '1px solid var(--c-border-subtle, var(--c-border))',
        borderRadius: 8,
        padding: '10px 12px',
        marginBottom: 10,
        maxHeight: 240,
        overflowY: 'auto',
        lineHeight: 1.5,
      }}>
        {suggestion.text}
      </div>

      {!isDecided ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          <button onClick={handleAccept} className="btn btn-primary btn-sm" title={t('ai.suggestion.acceptHint')}>
            <Check size={14} /> {t('ai.suggestion.accept')}
          </button>
          <button onClick={handleEdit} className="btn btn-secondary btn-sm" title={t('ai.suggestion.editHint')}>
            <Edit2 size={14} /> {t('ai.suggestion.edit')}
          </button>
          <button onClick={handleUsefulSkip} className="btn btn-ghost btn-sm" title={t('ai.suggestion.usefulSkipHint')}>
            <ThumbsUp size={14} /> {t('ai.suggestion.usefulSkip')}
          </button>
          <button onClick={handleReject} className="btn btn-ghost btn-sm" style={{ color: '#ef4444' }} title={t('ai.suggestion.rejectHint')}>
            <XIcon size={14} /> {t('ai.suggestion.reject')}
          </button>
          <button onClick={handleRegenerate} className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} disabled={loading} title={t('ai.suggestion.regenerate')}>
            <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--c-text-muted)' }}>
          <span>{statusLabel(suggestion.status, t)}</span>
          <button onClick={handleRegenerate} className="btn btn-ghost btn-sm" style={{ padding: '2px 8px' }}>
            <RefreshCw size={12} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
            {t('ai.suggestion.regenerate')}
          </button>
        </div>
      )}
    </div>
  );
}

function statusLabel(status, t) {
  if (status === 'accepted') return t('ai.suggestion.statusAccepted');
  if (status === 'edited') return t('ai.suggestion.statusEdited');
  if (status === 'useful_skipped') return t('ai.suggestion.statusUsefulSkipped');
  if (status === 'rejected') return t('ai.suggestion.statusRejected');
  if (status === 'sent') return t('ai.suggestion.statusSent');
  return '';
}

// ─── compact-only styles ────────────────────────────────────────────
function compactPillStyle(accent) {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    background: 'var(--c-surface)',
    border: '1px solid var(--c-border)',
    borderLeft: accent ? `3px solid ${accent}` : '1px solid var(--c-border)',
    borderRadius: 8,
    minHeight: 28,
  };
}

const iconBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  padding: 3,
  borderRadius: 4,
  cursor: 'pointer',
  color: 'var(--c-text-muted)',
};

const primaryMiniBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 10px',
  background: 'var(--c-brand)',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 11.5,
  fontWeight: 600,
  cursor: 'pointer',
};

const ghostMiniBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 8px',
  background: 'transparent',
  color: 'var(--c-text)',
  border: '1px solid var(--c-border)',
  borderRadius: 6,
  fontSize: 11.5,
  cursor: 'pointer',
};
